import { initializeApp } from "firebase/app";
import { getAuth, signInWithEmailLink, signOut } from "firebase/auth";
import { getAuthFirebaseConfig } from "../data/DataStores";

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? 'http://localhost:8082' : 'https://api.rebuild.work');

// Initialize a separate Firebase app for authentication
const authApp = initializeApp(getAuthFirebaseConfig(), "authApp");
export const auth = getAuth(authApp);

export interface SendSignInLinkResponse {
  success: boolean;
  data?: any;
  error?: string;
}

interface LoginWithLinkResult {
  success: boolean;
  error?: string;
}

class AuthService {
  private static PENDING_EMAIL_KEY = "pendingEmail";

  // Pending email management (for passwordless flow)
  async setPendingEmail(email: string): Promise<void> {
    window.localStorage.setItem(AuthService.PENDING_EMAIL_KEY, email);
  }

  async getPendingEmail(): Promise<string | null> {
    try {
      return window.localStorage.getItem(AuthService.PENDING_EMAIL_KEY);
    } catch (e) {
      return null;
    }
  }

  async clearPendingEmail(): Promise<void> {
    window.localStorage.removeItem(AuthService.PENDING_EMAIL_KEY);
  }

  // Authentication API methods
  async sendSignInLink(email: string): Promise<SendSignInLinkResponse> {
    try {
      const frontendOrigin = window.location.origin;
      const encodedEmail = encodeURIComponent(email.trim());

      const url = `${API_BASE_URL}/rest/access/sendSignInLink?origin=${frontendOrigin}&email=${encodedEmail}&mobile=false`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (data.success) {
        // Store the email for later validation
        await this.setPendingEmail(email.trim());
        return {
          success: true,
          data: data.data,
        };
      } else {
        return {
          success: false,
          error: data.error || "Failed to send sign-in link",
        };
      }
    } catch (error) {
      console.error("Send sign-in link error:", error);
      return {
        success: false,
        error: "Network error. Please check your connection and try again.",
      };
    }
  }

  // Method to login with 6-digit code
  async loginWithCode(code: string): Promise<LoginWithLinkResult> {
    try {
      // First, validate the code and get the actual Firebase sign-in link.
      // The server keys stored codes by `${email}:${code}`, so include the pending email.
      const pendingEmail = await this.getPendingEmail();
      const codeParam = encodeURIComponent(code.trim());
      const emailParam = pendingEmail ? `&email=${encodeURIComponent(pendingEmail)}` : "";
      const url = `${API_BASE_URL}/rest/access/checkCode?code=${codeParam}${emailParam}`;

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();

      if (!data.success || !data.data) {
        return {
          success: false,
          error: data.error || "Invalid code. Please check the code and try again.",
        };
      }

      // The API might return an object with both email and link, or just the link
      let firebaseLink: string;
      let email: string | undefined;

      if (typeof data.data === "string") {
        // If data.data is just a string, it's the Firebase link
        firebaseLink = data.data;
      } else if (data.data && typeof data.data === "object") {
        // If it's an object, extract the link and email
        firebaseLink = data.data.link || data.data.firebaseLink || data.data;
        email = data.data.email;

        // If the API provides the email, update the pending email
        if (email) {
          await this.setPendingEmail(email);
        }
      } else {
        return {
          success: false,
          error: "Invalid response format from server.",
        };
      }

      // Now use the returned link to sign in with Firebase
      return await this.loginWithLink(firebaseLink);
    } catch (error) {
      console.error("Code validation error:", error);
      return {
        success: false,
        error: "Network error. Please check your connection and try again.",
      };
    }
  }

  // Single method to login with any type of sign-in link using Firebase
  async loginWithLink(link: string): Promise<LoginWithLinkResult> {
    try {
      // Ensure link is a string and not empty
      if (!link || typeof link !== "string") {
        console.error("Invalid link type:", typeof link, link);
        return {
          success: false,
          error: "Invalid sign-in link provided.",
        };
      }

      // Get the pending email from localStorage for validation
      const pendingEmail = await this.getPendingEmail();

      if (!pendingEmail) {
        return {
          success: false,
          error: "No pending sign-in request found. Please request a new sign-in link.",
        };
      }

      // Use Firebase JS SDK to validate the email link and sign in
      const result = await signInWithEmailLink(auth, pendingEmail, link.trim());
      if (!result || !result.user) {
        throw new Error("Auth failed");
      }

      // Clear pending email
      await this.clearPendingEmail();

      return {
        success: true,
      };
    } catch (error) {
      console.error("Firebase login with link error:", error);
      // Handle specific Firebase errors
      let errorMessage = "Invalid sign-in link. Please check the link and try again.";

      if (error instanceof Error) {
        if (error.message.includes("invalid-email")) {
          errorMessage = "Invalid email address.";
        } else if (error.message.includes("invalid-action-code")) {
          errorMessage = "Invalid or expired sign-in link. Please request a new code.";
        } else if (error.message.includes("user-disabled")) {
          errorMessage = "This account has been disabled.";
        }
      }

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  async logout(): Promise<void> {
    return signOut(auth);
  }
}

export const authService = new AuthService();
